import PropTypes from 'prop-types';
import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
// @mui
import {
  Box,
  List,
  Badge,
  Button,
  Tooltip,
  Divider,
  Typography,
  IconButton,
  ListItemText,
  ListItemButton,
  Tabs,
  Tab,
  Stack,
  Alert,
} from '@mui/material';
// hooks
import useLanguage from '../../../hooks/useLanguage';
// utils
import { fToNow } from '../../../utils/formatTime';
// components
import Iconify from '../../../components/Iconify';
import Scrollbar from '../../../components/Scrollbar';
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';
// redux - alerts
import {
  fetchAlertsRequest,
  markAlertReadRequest,
  markAllAlertsReadRequest,
} from '../../../redux/slices/mycore/alertsActions';
// redux - messages
import {
  fetchMessagesRequest,
  fetchUnreadCountRequest as fetchMessagesUnreadCountRequest,
  markMessageReadRequest,
  markAllReadRequest as markAllMessagesReadRequest,
} from '../../../redux/messages/actions';
// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
// utils
import { AxiosCall } from '../../../utils/axios';

// ----------------------------------------------------------------------

export default function MyCoreAlertsPopover() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getLocalizedPath } = useLanguage();

  // Alerts state
  const { notifiedAlerts = [], popoverAlertList = [], list: alertsList = [] } = useSelector((state) => state.mycore?.alerts || {});
  const alertsUnreadCount = Array.isArray(notifiedAlerts) ? notifiedAlerts.length : 0;

  // Messages state
  const { unreadCount: messagesUnreadCount, list: messagesList } = useSelector((state) => state.messages || {});

  const [open, setOpen] = useState(null);
  const [currentTab, setCurrentTab] = useState('alerts');

  // Pending Telegram link requests
  const [pendingTelegramLinks, setPendingTelegramLinks] = useState([]);

  const fetchPendingTelegramLinks = useCallback(async () => {
    try {
      const response = await AxiosCall('/sporeuser/pending_telegram_links/');
      setPendingTelegramLinks(response.data || []);
    } catch (error) {
      // Silently ignore - user may not be authenticated or endpoint may not exist
    }
  }, []);

  // Combined unread count for badge
  const totalUnreadCount = alertsUnreadCount + (messagesUnreadCount || 0) + pendingTelegramLinks.length;

  useEffect(() => {
    // NOTE: We no longer fetch alerts unread count here
    // Alerts use useAlertNotifications hook for monitoring (no automatic polling)
    // Messages use the global notificationPoller from useNotificationPoller hook
    // Fetch once on mount, the poller handles periodic updates
    dispatch(fetchMessagesUnreadCountRequest());

    // NO setInterval here - polling is handled by useNotificationPoller in DashboardLayout
  }, [dispatch]);

  useEffect(() => {
    // Fetch data when popover opens
    if (open) {
      dispatch(fetchAlertsRequest({ status: 'active', limit: 5 }));
      dispatch(fetchMessagesRequest());
      fetchPendingTelegramLinks();
    }
  }, [open, dispatch, fetchPendingTelegramLinks]);

  // Also fetch pending links on mount
  useEffect(() => {
    fetchPendingTelegramLinks();
  }, [fetchPendingTelegramLinks]);

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleMarkAllAsRead = () => {
    if (currentTab === 'alerts') {
      dispatch(markAllAlertsReadRequest());
    } else {
      dispatch(markAllMessagesReadRequest());
    }
  };

  const handleAlertClick = (alert) => {
    // Mark as read
    if (!alert.is_read) {
      dispatch(markAlertReadRequest(alert.id));
    }

    // Navigate to alerts page
    navigate(getLocalizedPath(PATH_DASHBOARD.mycolab.alerts));
    handleClose();
  };

  const handleMessageClick = (message) => {
    // Mark as read
    if (!message.is_read) {
      dispatch(markMessageReadRequest(message.id));
    }

    // Navigate to inbox
    navigate(getLocalizedPath('/dashboard/inbox'));
    handleClose();
  };

  const handleViewAll = () => {
    if (currentTab === 'alerts') {
      navigate(getLocalizedPath(PATH_DASHBOARD.mycolab.alerts));
    } else {
      navigate(getLocalizedPath('/dashboard/inbox'));
    }
    handleClose();
  };

  const handleTelegramLinkClick = () => {
    navigate(getLocalizedPath(PATH_DASHBOARD.settings.telegram));
    handleClose();
  };

  const handleChangeTab = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Get alerts from popover list (max 5)
  const unreadAlerts = Array.isArray(popoverAlertList) ? popoverAlertList.slice(0, 5) : [];

  // Get recent unread messages (max 5)
  const unreadMessages = messagesList.filter((msg) => !msg.is_read).slice(0, 5);

  // Current tab unread count
  const currentUnreadCount = currentTab === 'alerts' ? alertsUnreadCount : messagesUnreadCount;

  return (
    <>
      <IconButtonAnimate
        color={open ? 'primary' : 'default'}
        onClick={handleOpen}
        sx={{ width: 40, height: 40 }}
      >
        <Badge badgeContent={totalUnreadCount} color="error">
          <Iconify icon="eva:bell-fill" width={20} height={20} />
        </Badge>
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{ width: 360, p: 0, mt: 1.5, ml: 0.75 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">Notificaciones</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Tienes {totalUnreadCount} sin leer
            </Typography>
          </Box>

          {currentUnreadCount > 0 && (
            <Tooltip title="Marcar todas como leídas">
              <IconButton color="primary" onClick={handleMarkAllAsRead}>
                <Iconify icon="eva:done-all-fill" width={20} height={20} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {pendingTelegramLinks.length > 0 && (
          <Box sx={{ px: 2, py: 1.5, bgcolor: 'warning.lighter' }}>
            {pendingTelegramLinks.map((req) => (
              <Alert
                key={req.id}
                severity="warning"
                sx={{ mb: pendingTelegramLinks.length > 1 ? 1 : 0, cursor: 'pointer', '&:last-child': { mb: 0 } }}
                icon={<Iconify icon="ic:baseline-telegram" width={22} height={22} />}
                onClick={handleTelegramLinkClick}
              >
                <Typography variant="subtitle2" sx={{ fontSize: '0.8rem' }}>
                  {req.display_name} quiere vincular su Telegram
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Toca para aceptar o rechazar
                </Typography>
              </Alert>
            ))}
          </Box>
        )}

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Tabs
          value={currentTab}
          onChange={handleChangeTab}
          sx={{ px: 2 }}
        >
          <Tab
            label={`Alertas (${alertsUnreadCount || 0})`}
            value="alerts"
            sx={{ textTransform: 'none' }}
          />
          <Tab
            label={`Mensajes (${messagesUnreadCount || 0})`}
            value="messages"
            sx={{ textTransform: 'none' }}
          />
        </Tabs>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Scrollbar sx={{ height: { xs: 340, sm: 'auto' }, maxHeight: 400 }}>
          {currentTab === 'alerts' && (
            <>
              {unreadAlerts.length === 0 ? (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No hay alertas sin leer
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {unreadAlerts.map((alert) => (
                    <AlertItem key={alert.id} alert={alert} onClick={() => handleAlertClick(alert)} />
                  ))}
                </List>
              )}
            </>
          )}

          {currentTab === 'messages' && (
            <>
              {unreadMessages.length === 0 ? (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No hay mensajes sin leer
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {unreadMessages.map((message) => (
                    <MessageItem key={message.id} message={message} onClick={() => handleMessageClick(message)} />
                  ))}
                </List>
              )}
            </>
          )}
        </Scrollbar>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Box sx={{ p: 1 }}>
          <Button fullWidth disableRipple onClick={handleViewAll}>
            {currentTab === 'alerts' ? 'Ver Todas las Alertas' : 'Ver Todos los Mensajes'}
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
}

// ----------------------------------------------------------------------

MessageItem.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    message: PropTypes.string,
    created_at: PropTypes.string,
    is_read: PropTypes.bool,
  }),
  onClick: PropTypes.func,
};

function MessageItem({ message, onClick }) {
  return (
    <ListItemButton
      onClick={onClick}
      sx={{
        py: 1.5,
        px: 2.5,
        mt: '1px',
        ...(!message.is_read && {
          bgcolor: 'action.selected',
        }),
      }}
    >
      <Box
        sx={{
          mr: 2,
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          bgcolor: 'background.neutral',
        }}
      >
        <Iconify icon="eva:email-fill" width={24} height={24} sx={{ color: 'info.main' }} />
      </Box>
      <ListItemText
        primary={
          <Typography variant="subtitle2" noWrap>
            {message.title}
          </Typography>
        }
        secondary={
          <>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {message.message}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                mt: 0.5,
                display: 'flex',
                alignItems: 'center',
                color: 'text.disabled',
              }}
            >
              <Iconify icon="eva:clock-outline" sx={{ mr: 0.5, width: 16, height: 16 }} />
              {fToNow(message.created_at)}
            </Typography>
          </>
        }
      />
    </ListItemButton>
  );
}

// ----------------------------------------------------------------------

AlertItem.propTypes = {
  alert: PropTypes.shape({
    id: PropTypes.number,
    alert_type: PropTypes.string,
    alert_type_display: PropTypes.string,
    message: PropTypes.string,
    created_at: PropTypes.string,
    batch_name: PropTypes.string,
    is_read: PropTypes.bool,
  }),
  onClick: PropTypes.func,
};

function AlertItem({ alert, onClick }) {
  const getAlertIcon = (type) => {
    switch (type) {
      case 'contamination':
        return { icon: 'eva:alert-circle-fill', color: 'error.main' };
      case 'overdue':
        return { icon: 'eva:clock-fill', color: 'warning.main' };
      case 'system':
        return { icon: 'eva:info-fill', color: 'info.main' };
      default:
        return { icon: 'eva:bell-fill', color: 'primary.main' };
    }
  };

  const { icon, color } = getAlertIcon(alert.alert_type);

  return (
    <ListItemButton
      onClick={onClick}
      sx={{
        py: 1.5,
        px: 2.5,
        mt: '1px',
        ...(!alert.is_read && {
          bgcolor: 'action.selected',
        }),
      }}
    >
      <Box
        sx={{
          mr: 2,
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          bgcolor: 'background.neutral',
        }}
      >
        <Iconify icon={icon} width={24} height={24} sx={{ color }} />
      </Box>
      <ListItemText
        primary={
          <Typography variant="subtitle2" noWrap>
            {alert.alert_type_display}
            {alert.batch_name && (
              <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
                {' '}- {alert.batch_name}
              </Typography>
            )}
          </Typography>
        }
        secondary={
          <>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {alert.message}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                mt: 0.5,
                display: 'flex',
                alignItems: 'center',
                color: 'text.disabled',
              }}
            >
              <Iconify icon="eva:clock-outline" sx={{ mr: 0.5, width: 16, height: 16 }} />
              {fToNow(alert.created_at)}
            </Typography>
          </>
        }
      />
    </ListItemButton>
  );
}
